import React, {useEffect, useState} from 'react';
import SidebarLayout from '../../dashboard/components/SidebarLayout';
import {PermissionDeniedCard} from '../../../Presentation/components/common';
import Card from '../../../Presentation/components/ui/Card';
import Tabs from '../../../Presentation/components/ui/Tabs';
import {AppText} from '../../../Presentation/components/ui/Typography';
import {CreateUserRequest, UpdateUserRequest, DashboardUser} from '../models/user.model';
import {useUserManagement} from '../hooks/useUserManagement';
import {useNotification} from '../../../Services/useNotification';
import {useAuthorization} from '../../auth/hooks/useAuthorization';
import {PERMISSIONS} from '../../../constants/permissions';

import {UsersPageHeader} from '../components/headers/UsersPageHeader';
import {UsersListFilters} from '../components/filters/UsersListFilters';
import {UsersTable} from '../components/tables/UsersTable';
import {AddUserModal} from '../components/modals/AddUserModal';
import {EditUserModal} from '../components/modals/EditUserModal';
import ResetUserPasswordModal from '../components/modals/ResetUserPasswordModal';
import {PermissionsManagementNew} from '../components/management/PermissionsManagementNew';
import {UserStatistics} from '../components/management/UserStatistics';
import {RolesManagement} from '../components/management/RolesManagement';
import {Users, Shield, BarChart3, UserCog} from 'lucide-react';

type ActiveTab = 'list' | 'statistics' | 'roles' | 'permissions';

export const UsersManagementPage: React.FC = () => {
  const {success: showSuccess} = useNotification();
  const {hasPermission} = useAuthorization();

  const {
    // UI State
    activeTab,
    switchTab,

    // Modal States
    isAddUserModalOpen,
    isEditUserModalOpen,

    // Modal Actions
    openAddUserModal,
    closeAddUserModal,
    openEditUserModal,
    closeEditUserModal,

    // Selected Items
    selectedUser,
    setSelectedUser,

    // Search and Filters
    searchQuery,
    statusFilter,
    roleFilter,
    setSearchQuery,
    setStatusFilter,
    setRoleFilter,

    // Data
    users,
    pagination,

    // Loading States
    loading,
    creating,
    updating,

    // Error States
    error,
    createError,

    // Actions
    loadUsers,
    handleCreateUser,
    handleUpdateUser,
    handleDeleteUser,
    handleActivateUser,
    handleDeactivateUser,
    handleAssignPermissions,
    handleUpdateUserPassword,

    // Clear Actions
    clearError,
    clearCreateError
  } = useUserManagement();

  // Admin password-reset modal (sets a new password for another user)
  const [passwordUser, setPasswordUser] = useState<DashboardUser | null>(null);
  const [isPasswordModalOpen, setIsPasswordModalOpen] = useState(false);

  const openChangePasswordModal = (user: DashboardUser) => {
    setPasswordUser(user);
    setIsPasswordModalOpen(true);
  };

  // Fetch data on component mount
  useEffect(() => {
    loadUsers();
    // Don't call loadStatistics here as it's handled by tab switching
  }, [loadUsers]);

  // Check if user has permission to view users
  if (!hasPermission(PERMISSIONS.MANAGE_USERS)) {
    return (
      <SidebarLayout>
        <div className="p-6 min-h-screen rounded-xl bg-[#F2F3F5]">
          <PermissionDeniedCard message="ليس لديك صلاحيات للوصول إلى إدارة المستخدمين. يرجى التواصل مع المدير للحصول على الصلاحيات المناسبة." />
        </div>
      </SidebarLayout>
    );
  }

  const handleCreateUserSuccess = async (userData: CreateUserRequest) => {
    try {
      await handleCreateUser(userData);
      showSuccess('تم إنشاء المستخدم بنجاح');
      closeAddUserModal();
    } catch (error) {
      // Error notification is handled automatically by StandardizedErrorHandler
      // No need to manually show additional notifications
    }
  };

  const handleUpdateUserSuccess = async (userData: UpdateUserRequest) => {
    try {
      await handleUpdateUser(userData);
      showSuccess('تم تحديث المستخدم بنجاح');
      closeEditUserModal();
      // Refresh users list to ensure role information is updated
      await loadUsers();
    } catch (error) {
      // Error will be handled by EditUserModal's catch block
      // Re-throw to let the modal handle the error state and show notification
      throw error;
    }
  };

  const handleDeleteUserSuccess = async (userId: number) => {
    if (window.confirm('هل أنت متأكد من حذف هذا المستخدم؟')) {
      try {
        await handleDeleteUser(userId);
        showSuccess('تم حذف المستخدم بنجاح');
      } catch (error) {
        // Error notification is handled automatically by StandardizedErrorHandler
        // No need to manually show additional notifications
      }
    }
  };

  const handleActivateUserSuccess = async (userId: number) => {
    try {
      await handleActivateUser(userId);
      showSuccess('تم تفعيل المستخدم بنجاح');
    } catch (error) {
      // Error notification is handled automatically by StandardizedErrorHandler
      // No need to manually show additional notifications
    }
  };

  const handleDeactivateUserSuccess = async (userId: number) => {
    try {
      await handleDeactivateUser(userId);
      showSuccess('تم إلغاء تفعيل المستخدم بنجاح');
    } catch (error) {
      // Error notification is handled automatically by StandardizedErrorHandler
      // No need to manually show additional notifications
    }
  };

  const handleAssignPermissionsFromTab = async (userId: number, permissions: string[]) => {
    try {
      // Use the permission assignment functionality from the hook
      await handleAssignPermissions(userId, permissions);
      showSuccess('تم حفظ الصلاحيات بنجاح');
    } catch (error) {
      // Error notification is handled automatically by StandardizedErrorHandler
      // No need to manually show additional notifications
    }
  };

  const handleEditUser = (user: DashboardUser) => {
    setSelectedUser(user);
    openEditUserModal(user);
  };


  const handleTabChange = (tabKey: ActiveTab) => switchTab(tabKey);

  const handleOpenAddUserModal = () => openAddUserModal();

  const renderListTab = () => (
    <div className="space-y-6">
      {/* Filters */}
      <div className="bg-gray-50 p-6 rounded-lg">
        <UsersListFilters
          searchValue={searchQuery}
          onSearchChange={setSearchQuery}
          statusFilter={statusFilter}
          onStatusFilterChange={setStatusFilter}
          roleFilter={roleFilter}
          onRoleFilterChange={setRoleFilter}
        />
      </div>

      {/* Users Table */}
      <div className="bg-white rounded-lg border border-gray-200">
        <UsersTable
          data={users}
          page={pagination?.page || 1}
          pageSize={pagination?.limit || 10}
          totalCount={pagination?.total || 0}
          onPageChange={(_page) => {
            // Handle pagination through the unified hook
            loadUsers();
          }}
          onPageSizeChange={(_size) => {
            // Handle page size change through the unified hook
            loadUsers();
          }}
          onEdit={handleEditUser}
          onDelete={handleDeleteUserSuccess}
          onActivate={handleActivateUserSuccess}
          onDeactivate={handleDeactivateUserSuccess}
          onChangePassword={openChangePasswordModal}
          showPageSizeSelector={true}
          showPaginationInfo={true}
          loading={loading}
        />
      </div>
    </div>
  );


  const renderStatisticsTab = () => (
    <UserStatistics />
  );

  const renderRolesTab = () => (
    <RolesManagement
      users={users}
      onAssignPermissions={handleAssignPermissionsFromTab}
    />
  );

  const renderPermissionsTab = () => (
    <PermissionsManagementNew
      users={users}
      onAssignPermissions={handleAssignPermissionsFromTab}
    />
  );

  const tabItems = [
    hasPermission(PERMISSIONS.MANAGE_USERS) && {
      key: 'list' as const,
      label: 'قائمة المدراء',
      description: 'عرض وإدارة جميع المدراء',
      icon: <Users className="w-5 h-5" />,
      badge: pagination?.total ?? users?.length ?? 0,
      content: <div className="p-6">{renderListTab()}</div>
    },
    hasPermission(PERMISSIONS.VIEW_DASHBOARD) && {
      key: 'statistics' as const,
      label: 'إحصائيات المستخدمين',
      description: 'إحصائيات وتحليلات المستخدمين',
      icon: <BarChart3 className="w-5 h-5" />,
      content: <div className="p-6">{renderStatisticsTab()}</div>
    },
    hasPermission(PERMISSIONS.ASSIGN_ROLES) && {
      key: 'roles' as const,
      label: 'إدارة الأدوار',
      description: 'إدارة أدوار المستخدمين وصلاحياتهم',
      icon: <UserCog className="w-5 h-5" />,
      content: <div className="p-6">{renderRolesTab()}</div>
    },
    hasPermission(PERMISSIONS.SYSTEM_SETTINGS) && {
      key: 'permissions' as const,
      label: 'إدارة الصلاحيات',
      description: 'إدارة صلاحيات النظام',
      icon: <Shield className="w-5 h-5" />,
      content: <div className="p-6">{renderPermissionsTab()}</div>
    }
  ].filter(Boolean) as {key: ActiveTab; label: string; description: string; icon: React.ReactNode; badge?: number; content: React.ReactNode}[];

  return (
    <SidebarLayout>
      <div className="space-y-6">
        {/* Page Header */}
        <UsersPageHeader
          onAddUser={handleOpenAddUserModal}
          onRefresh={loadUsers}
          refreshing={loading}
          totalUsers={users?.length || 0}
        />

        {/* Error is handled by StandardizedErrorHandler and shown as toast notification */}

        {createError && (
          <Card className="p-6 bg-red-50 border border-red-200 rounded-lg">
            <AppText className="text-red-700 leading-relaxed">{createError}</AppText>
            <button
              onClick={clearCreateError}
              className="mt-3 text-sm text-red-600 hover:text-red-800 font-medium"
            >
              إغلاق
            </button>
          </Card>
        )}

        <Card className="p-0 overflow-hidden shadow-sm">
          <Tabs
            items={tabItems}
            defaultActiveKey={activeTab}
            onChange={(key) => handleTabChange(key as ActiveTab)}
            variant="modern"
            className="flex-1"
          />
        </Card>

        {/* Add User Modal */}
        <AddUserModal
          open={isAddUserModalOpen}
          onClose={closeAddUserModal}
          onSubmit={handleCreateUserSuccess}
          loading={creating}
        />

        {/* Edit User Modal */}
        <EditUserModal
          open={isEditUserModalOpen}
          onClose={closeEditUserModal}
          onSubmit={handleUpdateUserSuccess}
          loading={updating}
          user={selectedUser}
        />

        {/* Admin Reset Password Modal */}
        <ResetUserPasswordModal
          open={isPasswordModalOpen}
          user={passwordUser}
          onClose={() => setIsPasswordModalOpen(false)}
          onSubmit={handleUpdateUserPassword}
        />
      </div>
    </SidebarLayout>
  );
};
